import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate, useLocation} from 'react-router'
import {useSelector} from 'react-redux'
import {useAuth0} from '@auth0/auth0-react'
import {Button} from '../../../../accountManagement/shared/Button'
import {CountrySelector} from '../../../../accountManagement/features/CountrySelector/CountrySelector'
import {COUNTRIES} from '../../../../accountManagement/features/CountrySelector/CountrySelector.utils'
import {
  isPartnerAccessCountry,
  useCountryRegistry,
} from '../../../../utils/country-registry'
import {
  CatalogContentAreaWrapper,
  CatalogContentArea,
  OrderSummaryColumn,
  StepCardWrapper,
  StepCard,
  FooterRow,
  FooterCenter,
} from '../../../../accountManagement/features/AccountSetup/AccountSetup.styles'
import {
  Header,
  BrandSection,
  LogoMark,
  TitleGroup,
  Title,
  Subtitle,
  Actions,
  CatalogWrapper,
  ProductsPanel,
  CatalogLoadingOverlay,
} from '../../../../pages/PurchaseLink/features/PurchaseLinkBuilder/ProductsCatalog/ProductsCatalog.styles'
import Icon from '../../../../core/shared/Icon'
import {CatalogGrid} from '../../../../features/CatalogGrid'
import {RetainWebsiteDowngradeModal} from '../../../catalog-components/retain-website-downgrade-modal'
import {Spinner} from '../../../loader-spinner'
import {useAuthClient} from '../../../../utils/use-auth-client'
import {useUserFeatures} from '../../../../context/user-features-context'
import {
  selectPartnerAC,
  selectUserRoles,
  selectBundleId,
} from '../../../../app-store/selector'
import {selectedInfluencerName} from '../../../../app-store/kpGlobalStateSlice'
import {kP11LinkService} from '../kp-11Link-utility/kp11-service'
import {MissingKeapPartnerIdModal} from '../kp-11Link-layout'
import {useManyLinkCatalog} from './useManyLinkCatalog'
import {buildManyLinkPayload} from './manyLinkPayload'
import {KPManyLinkSummary} from './KPManyLinkSummary'
import {
  PageWrapper,
  ContentArea,
} from '../../../../accountManagement/Accounts/Accounts.styles'
import {
  ManyLinkFlowWrapper,
  ConfirmBackdrop,
  ConfirmDialog,
} from './kp-many-styles'

export function KPManyLinkPage({keapPartnerId: routeKeapPartnerId, userData}) {
  const navigate = useNavigate()
  const location = useLocation()
  const client = useAuthClient()
  const {user} = useAuth0()
  const {Enable_GB_Loc_Access} = useUserFeatures()
  const partnerAffiliateCode = useSelector(selectPartnerAC)
  const {switchedRole, isSwitchRoleUser} = useSelector(selectUserRoles)
  const bundleId = useSelector(selectBundleId)
  const influencerName = useSelector(selectedInfluencerName)

  const selectedCountry = location.state?.selectedCountry || 'US'
  const [pendingCountry, setPendingCountry] = useState(null)
  const [partnerDetails, setPartnerDetails] = useState(null)

  const {
    items,
    coupons,
    activeItem,
    updateActiveItem,
    refreshPaymentPreview,
    isNewTLMCAccessible,
    isNewCustomer,
    productConfigInfo,
    loading,
    isInfluencerOps,
    softwareGroups,
    addonGroups,
    alternatePlanCodesToHide,
    allPlansUnfiltered,
    isDataLoaded,
    selectedProducts,
    selectedProductsByCode,
    disabledPlanCodes,
    validExtensionList,
    lockedBillingByGroup,
    showMCGroupAnnualToggle,
    isSocialContentGenGrandfathered,
    showGrowthPackageOnboarding,
    handleAddProduct,
    handleUpdateProduct,
    handleQuantityChange,
    handleRemoveProduct,
    pendingWebsiteDowngradePrompt,
    handleWebsiteDowngradeAnswer,
  } = useManyLinkCatalog({selectedCountry})

  useEffect(() => {
    let cancelled = false
    kP11LinkService
      .fetchKeapPartnerUserDetails(null, client, user)
      .then(resp => {
        if (!cancelled) setPartnerDetails(resp?.user_metadata || {})
      })
      .catch(() => {
        if (!cancelled) setPartnerDetails({})
      })
    return () => {
      cancelled = true
    }
  }, [client, user])

  // Same priority ladder as the legacy generator (kp-11Link-generateUrl).
  const resolvedKeapPartnerId = isInfluencerOps
    ? user?.email
    : isSwitchRoleUser && switchedRole?.[0] === 'KeapPartner'
    ? partnerAffiliateCode
    : routeKeapPartnerId || partnerDetails?.keapPartnerId

  const registry = useCountryRegistry()
  const countries = useMemo(
    () =>
      COUNTRIES.filter(
        c =>
          isPartnerAccessCountry(c.code) &&
          (c.code !== 'GB' || Enable_GB_Loc_Access),
      ),
    [Enable_GB_Loc_Access, registry],
  )

  const hasUserItems = items.some(item => !item.alreadyIncluded)

  // The cart slice used to clear itself on a country change; the shared cart
  // lives in the page hook, which resets whenever selectedCountry changes.
  const applyCountry = code => {
    navigate(location.pathname, {
      replace: true,
      state: {...(location.state || {}), selectedCountry: code},
    })
  }
  const handleCountryChange = code => {
    if (!code || code === selectedCountry) return
    if (hasUserItems) {
      setPendingCountry(code)
      return
    }
    applyCountry(code)
  }

  const totalToday = useMemo(
    () =>
      items
        .filter(item => !item.alreadyIncluded)
        .reduce((sum, item) => sum + Number(item.unitPrice || 0), 0),
    [items],
  )
  const isGenerateDisabled = isInfluencerOps
    ? !influencerName || totalToday === 0
    : !hasUserItems

  const buildPayload = useCallback(
    () =>
      buildManyLinkPayload({
        items,
        productConfigInfo,
        country: selectedCountry,
        keapPartnerId: resolvedKeapPartnerId,
        sfPartnerAccountId: partnerDetails?.sfPartnerAccountId,
        partnerEmail: user?.email,
        // plan incentives are the auto-applied coupons; the onboarding waiver
        // and anything the rep adds travel as couponCode, as before
        couponCodes: coupons.filter(c => !c._autoApplied).map(c => c.code),
        incentiveCodes: coupons.filter(c => c._autoApplied).map(c => c.code),
        influencerName: isInfluencerOps ? influencerName : undefined,
        bundleId,
      }),
    [
      items,
      productConfigInfo,
      selectedCountry,
      resolvedKeapPartnerId,
      partnerDetails,
      user,
      coupons,
      isInfluencerOps,
      influencerName,
      bundleId,
    ],
  )

  const partnerIdFromMetadata =
    userData?.user_metadata?.keapPartnerId || partnerDetails?.keapPartnerId
  if (
    !isInfluencerOps &&
    !routeKeapPartnerId &&
    !partnerIdFromMetadata &&
    !partnerAffiliateCode &&
    partnerDetails !== null
  ) {
    return <MissingKeapPartnerIdModal />
  }

  return (
    <PageWrapper data-theme="ooe-v1">
      <ManyLinkFlowWrapper>
        <ContentArea>
          <CatalogContentAreaWrapper>
            <CatalogContentArea>
              <StepCardWrapper>
                <StepCard isCatalogStep>
                  <Header style={{width: '100%', padding: '10px'}}>
                    <BrandSection>
                      <LogoMark aria-hidden="true">
                        <Icon name="product-catalog" size="lg" />
                      </LogoMark>
                      <TitleGroup>
                        <Title>Product Catalog</Title>
                        <Subtitle>Partner catalog</Subtitle>
                      </TitleGroup>
                    </BrandSection>
                    <Actions>
                      <CountrySelector
                        value={selectedCountry}
                        onChange={handleCountryChange}
                        countries={countries}
                      />
                    </Actions>
                  </Header>
                  <CatalogWrapper>
                    <ProductsPanel>
                      {loading && (
                        <CatalogLoadingOverlay>
                          <Spinner />
                        </CatalogLoadingOverlay>
                      )}
                      <CatalogGrid
                        key={selectedCountry}
                        softwareGroups={softwareGroups
                          .map(group => ({
                            ...group,
                            plans: (group.plans ?? []).filter(
                              plan => !alternatePlanCodesToHide.has(plan.code),
                            ),
                          }))
                          .filter(group => group.plans.length > 0)}
                        addonGroups={addonGroups}
                        allPlans={allPlansUnfiltered}
                        productConfigInfo={productConfigInfo}
                        isDataLoaded={isDataLoaded}
                        selectedCountry={selectedCountry}
                        selectedProducts={selectedProducts}
                        selectedProductsByCode={selectedProductsByCode}
                        disabledPlanCodes={disabledPlanCodes}
                        validExtensionList={validExtensionList}
                        lockedBillingByGroup={lockedBillingByGroup}
                        showMCGroupAnnualToggle={showMCGroupAnnualToggle}
                        isSocialContentGenGrandfathered={
                          isSocialContentGenGrandfathered
                        }
                        showGrowthPackageOnboarding={
                          showGrowthPackageOnboarding
                        }
                        minColumns={3}
                        combineNonKeapAddonTabs
                        onAddProduct={handleAddProduct}
                        onUpdateProduct={handleUpdateProduct}
                        onQuantityChange={handleQuantityChange}
                      />
                    </ProductsPanel>
                  </CatalogWrapper>
                </StepCard>

                <FooterRow>
                  <FooterCenter>
                    <Button
                      variant="secondary-gray"
                      size="sm"
                      onClick={() => navigate('/')}
                    >
                      Cancel
                    </Button>
                  </FooterCenter>
                </FooterRow>
              </StepCardWrapper>
            </CatalogContentArea>

            <OrderSummaryColumn>
              <KPManyLinkSummary
                ready={!loading}
                activeItem={activeItem}
                selectedCountry={selectedCountry}
                selectedProducts={selectedProducts}
                handleRemoveProduct={handleRemoveProduct}
                isNewTLMCAccessible={isNewTLMCAccessible}
                isNewCustomer={isNewCustomer}
                refreshPaymentPreview={refreshPaymentPreview}
                updateActiveItem={updateActiveItem}
                isInfluencerOps={isInfluencerOps}
                bundleId={bundleId}
                isGenerateDisabled={isGenerateDisabled}
                buildPayload={buildPayload}
              />
            </OrderSummaryColumn>
          </CatalogContentAreaWrapper>
        </ContentArea>

        <RetainWebsiteDowngradeModal
          isOpen={pendingWebsiteDowngradePrompt !== null}
          downgradedFrom={pendingWebsiteDowngradePrompt?.downgradedFromName}
          onConfirm={handleWebsiteDowngradeAnswer}
        />

        {pendingCountry && (
          <ConfirmBackdrop role="presentation">
            <ConfirmDialog
              role="dialog"
              aria-modal="true"
              aria-label="Change country"
            >
              <h3>Change country?</h3>
              <p>
                Plans and pricing differ by country. Switching will clear the
                items currently in your order summary.
              </p>
              <div className="actions">
                <Button
                  variant="secondary-gray"
                  size="sm"
                  onClick={() => setPendingCountry(null)}
                >
                  Keep current
                </Button>
                <Button
                  variant="solid-gradiant"
                  size="sm"
                  onClick={() => {
                    const code = pendingCountry
                    setPendingCountry(null)
                    applyCountry(code)
                  }}
                >
                  Change country
                </Button>
              </div>
            </ConfirmDialog>
          </ConfirmBackdrop>
        )}
      </ManyLinkFlowWrapper>
    </PageWrapper>
  )
}
